import React, { useState, useEffect } from 'react';
import { Search, Loader2, Phone, Lock } from 'lucide-react';
import { ChatConversation } from '../domain/types';

interface ConversationSidebarProps {
  conversations: ChatConversation[];
  selectedPhoneNumber?: string;
  onSelectConversation: (phoneNumber: string) => void;
  onSearch: (q: string) => void;
  loading: boolean;
  /** Increments every time a new real-time message arrives; triggers page-1 reset. */
  incomingMessageTick?: number;
}

export const ConversationSidebar: React.FC<ConversationSidebarProps> = ({
  conversations,
  selectedPhoneNumber,
  onSelectConversation,
  onSearch,
  loading,
  incomingMessageTick = 0,
}) => {
  const [visibleCount, setVisibleCount] = useState(15);
  const [searchQuery, setSearchQuery] = useState('');

  // When a new real-time message arrives, jump back to page 1 so the
  // conversation always appears at the top of the list (WhatsApp-style).
  useEffect(() => {
    if (incomingMessageTick > 0) {
      setVisibleCount(15);
    }
  }, [incomingMessageTick]);
  
  const sortedConversations = [...conversations].sort((a, b) => {
    const parseTime = (iso?: string) => {
      if (!iso) return 0;
      try {
        let str = iso;
        if (!str.endsWith('Z') && !str.includes('+') && !str.includes('-')) {
          str += 'Z';
        }
        return new Date(str).getTime();
      } catch (e) {
        return 0;
      }
    };
    const dateA = parseTime(a.last_message_at);
    const dateB = parseTime(b.last_message_at);
    return dateB - dateA;
  });

  const visibleConversations = sortedConversations.slice(0, visibleCount);

  const handleSearch = (val: string) => {
    setSearchQuery(val);
    setVisibleCount(15);
    onSearch(val);
  };

  const formatTime = (isoString?: string) => {
    if (!isoString) return '';
    
    try {
      let str = isoString;
      // Normalise: Mongodb might send naive UTC without suffix
      if (!str.endsWith('Z') && !str.includes('+') && !str.includes('-')) {
        str += 'Z';
      }
      
      const date = new Date(str);
      if (isNaN(date.getTime())) return '';

      // Get current time in Bogota for comparison
      const nowBogotaStr = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Bogota',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric'
      }).format(new Date());
      
      const dateBogotaStr = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Bogota',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric'
      }).format(date);

      const isToday = nowBogotaStr === dateBogotaStr;
      
      if (isToday) {
        return date.toLocaleTimeString('en-US', { 
          timeZone: 'America/Bogota', 
          hour: 'numeric', 
          minute: '2-digit', 
          hour12: true 
        }).toLowerCase().replace(/\s/g, "");
      }

      // Check for Yesterday (Ayer)
      const now = new Date();
      const yesterday = new Date(now);
      yesterday.setDate(now.getDate() - 1);
      
      const yesterdayBogotaStr = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Bogota',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric'
      }).format(yesterday);

      if (dateBogotaStr === yesterdayBogotaStr) {
        return 'Ayer';
      }

      // Default: show date
      return date.toLocaleDateString('es-CO', { 
        timeZone: 'America/Bogota', 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric' 
      });
    } catch (e) {
      console.error("[Inbox] formatTime error:", e);
      return '';
    }
  };

  const truncateMessage = (message?: string, maxLength: number = 40): string => {
    if (!message) return '(Sin mensaje)';
    return message.length > maxLength ? message.substring(0, maxLength) + '...' : message;
  };

  const formatPhoneNumber = (phone: string) => {
    if (!phone) return '';
    return phone.split('|')[0];
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    if (
      scrollTop + clientHeight >= scrollHeight - 40 &&
      visibleCount < sortedConversations.length
    ) {
      setVisibleCount((prev) =>
        Math.min(prev + 15, sortedConversations.length)
      );
    }
  };

  return (
    <div className="flex flex-col h-full w-full bg-white">
      {/* HEADER */}
      <div className="h-[72px] px-6 flex items-center justify-between border-b border-gray-200 bg-white shrink-0">
        <h2 className="text-xl font-bold text-gray-800">Conversaciones</h2>
      </div>

      {/* SEARCH BOX */}
      <div className="p-4 pb-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar contacto..."
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all placeholder:text-gray-400 text-sm"
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>
      </div>

      {/* LIST OF CONVERSATIONS */}
      <div
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto w-full p-2 space-y-1 scrollbar-hide"
        >
        {loading ? (
          <div className="flex justify-center p-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : conversations.length === 0 ? (
          <div className="p-6 text-center text-gray-400 text-sm">
            {searchQuery ? 'No se encontraron contactos.' : 'No hay conversaciones disponibles.'}
          </div>
        ) : (
          visibleConversations.map((conv) => (
            <button
              key={conv.phone_number}
              onClick={() => onSelectConversation(conv.phone_number)}
              className={`w-full text-left px-3 py-3 rounded-lg transition-all duration-200 border 
              ${conv.unread_count > 0
                ? 'bg-blue-50 border-blue-200 shadow-sm'
                : 'bg-white border-transparent hover:bg-gray-50 hover:border-gray-200'}
            `}
            >
              <div className="flex items-start justify-between gap-3">
                {/* Contact Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className={`block truncate text-sm flex-1 ${
                      conv.unread_count > 0
                        ? 'text-gray-900 font-bold'
                        : 'text-gray-800 font-medium'
                    }`}>
                      {conv.contact_name || formatPhoneNumber(conv.phone_number)}
                    </span>
                  </div>
                  
                  {/* Phone Number (smaller) */}
                  <div className="flex items-center gap-1 text-xs text-gray-400 mb-1">
                    <Phone className="w-3 h-3" />
                    <span>{formatPhoneNumber(conv.phone_number)}</span>
                    {!conv.can_reply && (
                      <div title="Ventana 24h cerrada" className="flex items-center">
                        <Lock className="w-3 h-3 text-red-400 ml-1" />
                      </div>
                    )}
                  </div>

                  {/* Message Preview */}
                  <p className={`text-xs truncate ${
                    conv.unread_count > 0
                      ? 'text-gray-900 font-bold'
                      : 'text-gray-500'
                  }`}>
                    {conv.direction === 'sent' ? 'Tú: ' : ''}{truncateMessage(conv.last_message)}
                  </p>
                </div>

                {/* Right Column */}
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <div className="text-xs text-gray-400">
                    {formatTime(conv.last_message_at)}
                  </div>
                </div>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
};
